import { getAccessToken } from "../auth/tokens";

/**
 * Fetch an invoice PDF as an authenticated blob and return an object URL.
 *
 * The PDF endpoint requires a Bearer token, which a plain new-tab navigation
 * can't carry — so we fetch it with the in-memory access token and hand back
 * a `blob:` URL the browser can open or print without auth.
 */
export async function fetchInvoicePdfUrl(txId: string): Promise<string> {
  const token = getAccessToken();
  const resp = await fetch(`/api/v1/transactions/${txId}/invoice.pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) {
    throw new Error(`Invoice PDF fetch failed: ${resp.status}`);
  }
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}

/** Open an invoice PDF in a new tab. Revokes the object URL after it loads. */
export async function openInvoicePdf(txId: string): Promise<void> {
  const url = await fetchInvoicePdfUrl(txId);
  const win = window.open(url, "_blank");
  // If the popup was blocked, fall back to navigating via a transient anchor.
  if (!win) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
  // Give the new tab time to load before releasing the blob.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
